/**
 * ProjectStorageEngine — persistência local com IndexedDB.
 *
 * Nada de áudio indo para servidor: o projeto (configurações, takes, marcadores,
 * notas, mixer, offsets) fica no dispositivo do usuário.
 *
 * Estratégia:
 *  - store "projects": metadados + documento serializável (sem Float32Array);
 *  - store "audio":    cada segmento gravado como Blob (binário) por id;
 *  - autosave com debounce e aviso de erro de cota (sem falhar em silêncio).
 */
import type { InstrumentalRef, ProjectDoc, RecordedSegment, Take } from '../types.js';

export const DB_NAME = 'vocal-studio';
export const DB_VERSION = 1;
const STORE_PROJECTS = 'projects';
const STORE_AUDIO = 'audio';
const STORE_META = 'meta';

export interface ProjectSummary {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  sourceType: ProjectDoc['sourceType'];
  youtubeVideoId?: string;
  youtubeTitle?: string;
  instrumentalName?: string;
  durationSec: number;
  takes: number;
  tracks: number;
  bytes: number;
}

export interface StoredAudioRef {
  id: string;
  projectId: string;
  kind: 'segment' | 'instrumental';
  takeId?: string;
  segmentId?: string;
  sampleRate: number;
  length: number;
  channels?: number;
  blob: Blob;
  savedAt: number;
}

function float32ToBlob(data: Float32Array): Blob {
  const copy = new Float32Array(data.length);
  copy.set(data);
  return new Blob([copy.buffer as ArrayBuffer], { type: 'application/octet-stream' });
}

async function blobToFloat32(blob: Blob): Promise<Float32Array> {
  const buf = await blob.arrayBuffer();
  return new Float32Array(buf);
}

export class ProjectStorageEngine {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private saveTimer: number | null = null;
  lastError: string | null = null;

  get available(): boolean {
    return typeof indexedDB !== 'undefined';
  }

  open(): Promise<IDBDatabase> {
    if (!this.available) return Promise.reject(new Error('IndexedDB indisponível neste navegador.'));
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
          const s = db.createObjectStore(STORE_PROJECTS, { keyPath: 'id' });
          s.createIndex('updatedAt', 'updatedAt');
        }
        if (!db.objectStoreNames.contains(STORE_AUDIO)) {
          const a = db.createObjectStore(STORE_AUDIO, { keyPath: 'id' });
          a.createIndex('projectId', 'projectId');
        }
        if (!db.objectStoreNames.contains(STORE_META)) {
          db.createObjectStore(STORE_META, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('Falha ao abrir o banco local.'));
      req.onblocked = () => reject(new Error('Outra aba está com o banco aberto. Feche e tente de novo.'));
    });
    return this.dbPromise;
  }

  private async tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const t = db.transaction(store, mode);
      const s = t.objectStore(store);
      const req = fn(s) as IDBRequest<T> | undefined;
      t.oncomplete = () => resolve(req ? (req.result as T) : (undefined as unknown as T));
      t.onerror = () => reject(t.error ?? new Error('Erro no banco local.'));
      t.onabort = () => reject(t.error ?? new Error('Operação abortada no banco local.'));
    });
  }

  // ------------------------------------------------------------------
  // Projetos
  // ------------------------------------------------------------------

  async listProjects(): Promise<ProjectSummary[]> {
    try {
      const rows = await this.tx<any[]>(STORE_PROJECTS, 'readonly', (s) => s.getAll() as IDBRequest<any[]>);
      return (rows ?? [])
        .map((r) => r.summary as ProjectSummary)
        .filter(Boolean)
        .sort((a, b) => b.updatedAt - a.updatedAt);
    } catch (err) {
      this.lastError = describeDbError(err);
      return [];
    }
  }

  async saveProject(doc: ProjectDoc, summary: ProjectSummary): Promise<void> {
    const audioRefs: StoredAudioRef[] = [];
    const takesOut: Take[] = [];
    for (const take of doc.takes) {
      const segs: RecordedSegment[] = [];
      for (const seg of take.segments) {
        const refId = `${doc.id}:${take.id}:${seg.id}`;
        audioRefs.push({
          id: refId,
          projectId: doc.id,
          kind: 'segment',
          takeId: take.id,
          segmentId: seg.id,
          sampleRate: seg.sampleRate,
          length: seg.samples.length,
          blob: float32ToBlob(seg.samples),
          savedAt: Date.now(),
        });
        segs.push({ ...seg, samples: new Float32Array(0) });
      }
      takesOut.push({ ...take, segments: segs });
    }

    let instrumentalRef: InstrumentalRef | null = doc.instrumental ?? null;
    let instAudioRef: StoredAudioRef | null = null;
    if (doc.instrumentalBuffer) {
      const b = doc.instrumentalBuffer;
      const channels = Math.min(2, b.numberOfChannels);
      const interleaved = new Float32Array(b.length * channels);
      for (let c = 0; c < channels; c++) {
        const d = b.getChannelData(c);
        for (let i = 0; i < b.length; i++) interleaved[i * channels + c] = d[i];
      }
      instAudioRef = {
        id: `${doc.id}:instrumental`,
        projectId: doc.id,
        kind: 'instrumental',
        sampleRate: b.sampleRate,
        length: b.length,
        channels,
        blob: float32ToBlob(interleaved),
        savedAt: Date.now(),
      };
      instrumentalRef = instrumentalRef ?? {
        id: `${doc.id}:instrumental`,
        name: 'instrumental',
        bytes: interleaved.byteLength,
        duration: b.duration,
        sampleRate: b.sampleRate,
        rightsAccepted: true,
        createdAt: Date.now(),
      };
    }

    // Remove referências mortas (takes excluídos pelo usuário)
    const keep = new Set(audioRefs.map((a) => a.id));
    if (instAudioRef) keep.add(instAudioRef.id);
    const existing = await this.audioIdsForProject(doc.id);
    const stale = existing.filter((id) => !keep.has(id));

    const serializable = {
      ...doc,
      takes: takesOut,
      instrumental: instrumentalRef,
      instrumentalBuffer: undefined,
      tracks: doc.tracks.map((t) => ({ ...t, buffer: undefined, peaks: null })),
      updatedAt: Date.now(),
    };

    await this.tx(STORE_PROJECTS, 'readwrite', (s) =>
      s.put({ id: doc.id, summary, doc: serializable, savedAt: Date.now() })
    );
    for (const ref of audioRefs) {
      await this.tx(STORE_AUDIO, 'readwrite', (s) => s.put(ref));
    }
    if (instAudioRef) {
      await this.tx(STORE_AUDIO, 'readwrite', (s) => s.put(instAudioRef!));
    }
    for (const id of stale) {
      await this.tx(STORE_AUDIO, 'readwrite', (s) => s.delete(id));
    }
    await this.setMeta('lastProjectId', doc.id);
  }

  async loadProject(id: string): Promise<ProjectDoc | null> {
    try {
      const row = await this.tx<any>(STORE_PROJECTS, 'readonly', (s) => s.get(id));
      if (!row?.doc) return null;
      const doc: ProjectDoc = row.doc;
      // reidrata takes
      const takes: Take[] = [];
      for (const take of doc.takes ?? []) {
        const segments: RecordedSegment[] = [];
        for (const seg of take.segments ?? []) {
          const refId = `${doc.id}:${take.id}:${seg.id}`;
          const audio = await this.tx<StoredAudioRef | undefined>(STORE_AUDIO, 'readonly', (s) => s.get(refId));
          if (audio?.blob) {
            const samples = await blobToFloat32(audio.blob);
            segments.push({ ...seg, samples, sampleRate: audio.sampleRate });
          }
        }
        takes.push({ ...take, segments });
      }
      doc.takes = takes;
      // reidrata instrumental
      const instAudio = await this.tx<StoredAudioRef | undefined>(STORE_AUDIO, 'readonly', (s) =>
        s.get(`${doc.id}:instrumental`)
      );
      if (instAudio?.blob) {
        const interleaved = await blobToFloat32(instAudio.blob);
        const channels = Math.max(1, Math.min(2, instAudio.channels ?? 1));
        const length = instAudio.length || Math.floor(interleaved.length / channels);
        const AC: typeof AudioContext = (globalThis as any).AudioContext || (globalThis as any).webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          const buf = ctx.createBuffer(channels, length, instAudio.sampleRate);
          for (let c = 0; c < channels; c++) {
            const channel = new Float32Array(length);
            for (let i = 0; i < length; i++) channel[i] = interleaved[i * channels + c] ?? 0;
            buf.copyToChannel(channel, c);
          }
          doc.instrumentalBuffer = buf;
          ctx.close().catch(() => {});
        }
      }
      return doc;
    } catch (err) {
      this.lastError = describeDbError(err);
      return null;
    }
  }

  async deleteProject(id: string): Promise<void> {
    const ids = await this.audioIdsForProject(id);
    for (const audioId of ids) {
      await this.tx(STORE_AUDIO, 'readwrite', (s) => s.delete(audioId));
    }
    await this.tx(STORE_PROJECTS, 'readwrite', (s) => s.delete(id));
  }

  private async audioIdsForProject(projectId: string): Promise<string[]> {
    const all = await this.tx<StoredAudioRef[]>(STORE_AUDIO, 'readonly', (s) => s.getAll() as IDBRequest<StoredAudioRef[]>);
    return (all ?? []).filter((a) => a.projectId === projectId).map((a) => a.id);
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await this.tx(STORE_META, 'readwrite', (s) => s.put({ key, value, at: Date.now() }));
  }

  async getMeta<T>(key: string): Promise<T | null> {
    try {
      const row = await this.tx<any>(STORE_META, 'readonly', (s) => s.get(key));
      return (row?.value as T) ?? null;
    } catch {
      return null;
    }
  }

  /** Estimativa de uso de armazenamento (quando o navegador expõe). */
  async estimate(): Promise<{ usage: number; quota: number } | null> {
    if (!navigator.storage?.estimate) return null;
    const e = await navigator.storage.estimate();
    return { usage: e.usage ?? 0, quota: e.quota ?? 0 };
  }

  async persist(): Promise<boolean> {
    if (!navigator.storage?.persist) return false;
    try {
      return await navigator.storage.persist();
    } catch {
      return false;
    }
  }

  /** Autosave com debounce — nunca bloqueia a UI. */
  scheduleSave(doc: ProjectDoc, summary: ProjectSummary, delayMs = 1200, onDone?: (err: string | null) => void) {
    if (this.saveTimer != null) clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(async () => {
      this.saveTimer = null;
      try {
        await this.saveProject(doc, summary);
        this.lastError = null;
        onDone?.(null);
      } catch (err) {
        this.lastError = describeDbError(err);
        onDone?.(this.lastError);
      }
    }, delayMs);
  }

  cancelScheduled() {
    if (this.saveTimer != null) clearTimeout(this.saveTimer);
    this.saveTimer = null;
  }
}

export function describeDbError(err: unknown): string {
  const e = err as { name?: string; message?: string };
  if (e?.name === 'QuotaExceededError')
    return 'Armazenamento local cheio. Exporte e apague projetos antigos para liberar espaço.';
  if (e?.name === 'InvalidStateError') return 'O armazenamento local está bloqueado neste navegador (modo privado?).';
  return e?.message ? `Falha ao salvar o projeto: ${e.message}` : 'Falha ao salvar o projeto no armazenamento local.';
}

export const projectStorage = new ProjectStorageEngine();
